import dayjs, { Dayjs } from "dayjs";
import { DATE_FORMAT_DDMMYYYY_HHMMSS, DATE_FORMAT_YY, DATE_FORMAT_YYYY_MM_DD } from "../constants/common";
import {
  InvoiceStatusEnum,
  MatchedFlagEnum,
  MatchedTeamEnum,
  SupplierInvoiceRecordStatusEnum,
  UserRoleRecordStatusEnum,
} from "../constants/enum";
import { EspWorkflowStatusEnum, MatchedStatusEnum } from "../constants/ap-workflow";

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const formatEuropeanCurrencyIntl = (value: string | number): string => {
  if (!value) return "";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "";

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

const isISODateString = (value: any): boolean => {
  return (
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value) && dayjs(value).isValid()
  );
};

export const formatISODateFields = (data: Record<string, any>): Record<string, any> => {
  const formattedData: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
      continue;
    }
    if (isISODateString(value)) {
      formattedData[key] = dayjs(value);
    } else {
      formattedData[key] = value;
    }
  }

  return formattedData;
};

function formatValueIfDate(value: any): any {
  if (value === null || value === "" || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((item) => formatValueIfDate(item));
  }

  if (dayjs.isDayjs(value)) {
    return (value as Dayjs).format(DATE_FORMAT_YYYY_MM_DD);
  }

  if (value instanceof Date) {
    return dayjs(value).format(DATE_FORMAT_YYYY_MM_DD);
  }

  if (typeof value === "string") {
    if (isLikelyDateString(value)) {
      const d = dayjs(value);
      if (d.isValid()) return d.format(DATE_FORMAT_YYYY_MM_DD);
    }
    return value;
  }

  return value;
}

function isLikelyDateString(s: string): boolean {
  if (!s || s.length < 8) return false;
  if (/^\d+$/.test(s)) return false;
  return /[-/TZ]/.test(s);
}

export const formatDateFieldsFlat = (obj: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => value !== null && value !== undefined && value !== "")
      .map(([key, value]) => [key, formatValueIfDate(value)])
  );
};

export const countNonEmptyFields = (data: Record<string, any>): number => {
  return Object.values(data).filter((value) => {
    if (value === null || value === undefined) return false;

    if (typeof value === "string" && value.trim() === "") return false;

    if (Array.isArray(value) && value.length === 0) return false;

    return true;
  }).length;
};

export const formatNumberFixed = (value: number | string): string => {
  const num = Number(value);
  if (isNaN(num)) return "";

  const [intPart, decimalPart] = num.toFixed(2).split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return `${formattedInt}.${decimalPart}`;
};

export const formatNumber = (value: number | string): string => {
  const num = Number(value);
  if (isNaN(num)) return "";

  const formattedInt = Math.round(num)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return formattedInt;
};

export const formatDateDDMMYYYY = (value: string | Date): string | null => {
  if (!value) return null;
  return dayjs(value).isValid() ? dayjs(value).format(DATE_FORMAT_YY) : null;
};

export const formatDateDDMMYYYY_HHMMSS = (value: string | Date): string | null => {
  if (!value) return null;
  return dayjs(value).isValid() ? dayjs(value).format(DATE_FORMAT_DDMMYYYY_HHMMSS) : null;
};

export const renderClassNameStatusEsp = (value: string) => {
  switch (value) {
    case "Unmatched":
    case "Query":
    case "Duplicated":
      return "text-warning";
    case "Exported":
      return "text-primary";
    case "Inactive":
    case "Rejected":
      return "text-error";
    default:
      return "text-success";
  }
};

export const renderClassNameStatusInvoice = (value: string) => {
  switch (value) {
    case InvoiceStatusEnum.Query:
      return "text-warning";
    case InvoiceStatusEnum.Duplicated:
      return "text-warning";
    case InvoiceStatusEnum.Rejected:
      return "text-error";
    default:
      return "text-success";
  }
};

export const listStatusInvoice = [
  InvoiceStatusEnum.Query,
  InvoiceStatusEnum.Duplicated,
  InvoiceStatusEnum.Accepted,
  InvoiceStatusEnum.Amended,
  InvoiceStatusEnum.Rejected,
  InvoiceStatusEnum.Matched,
  InvoiceStatusEnum.Exported,
];

export function getInvoiceStatuses(statusList: InvoiceStatusEnum[]): InvoiceStatusEnum[] {
  const hasAcceptedOrAmended =
    statusList.includes(InvoiceStatusEnum.Accepted) ||
    statusList.includes(InvoiceStatusEnum.Amended) ||
    statusList.includes(InvoiceStatusEnum.Exported) ||
    statusList.includes(InvoiceStatusEnum.Matched);

  let filteredList = hasAcceptedOrAmended
    ? Array.from(
        new Set([
          InvoiceStatusEnum.Accepted,
          ...statusList.filter(
            (s) =>
              s !== InvoiceStatusEnum.Amended &&
              s !== InvoiceStatusEnum.Accepted &&
              s !== InvoiceStatusEnum.Exported &&
              s !== InvoiceStatusEnum.Matched
          ),
        ])
      )
    : statusList;

  return filteredList.sort((a, b) => listStatusInvoice.indexOf(a) - listStatusInvoice.indexOf(b));
}

export const renderClassNameStatusSir = (value: string) => {
  switch (value) {
    case SupplierInvoiceRecordStatusEnum.UNMATCHED:
      return "text-warning";
    case SupplierInvoiceRecordStatusEnum.EXPORTED:
      return "text-primary";
    case SupplierInvoiceRecordStatusEnum.PENDING:
      return "text-grey";
    case SupplierInvoiceRecordStatusEnum.REJECTED:
      return "text-error";
    case SupplierInvoiceRecordStatusEnum.MATCHED:
      return "text-success";
    default:
      return "";
  }
};

export const listStatusSir = [
  SupplierInvoiceRecordStatusEnum.UNMATCHED,
  SupplierInvoiceRecordStatusEnum.REJECTED,
  SupplierInvoiceRecordStatusEnum.PENDING,
  SupplierInvoiceRecordStatusEnum.MATCHED,
  SupplierInvoiceRecordStatusEnum.EXPORTED,
];

export interface RoleOption {
  label: string;
  value: UserRoleRecordStatusEnum;
}

export const listRoleUser: RoleOption[] = [
  { label: "Ap Escalation", value: UserRoleRecordStatusEnum.AP_ESCALATION },
  { label: "Ap Owner", value: UserRoleRecordStatusEnum.AP_OWNER },
  { label: "Product Head", value: UserRoleRecordStatusEnum.PRODUCT_HEAD },
  { label: "Product Owner", value: UserRoleRecordStatusEnum.PRODUCT_OWNER },
];

export const listMatchedStatusTabReview = [
  MatchedStatusEnum.TO_CONFIRM,
  MatchedStatusEnum.FURTHER_ACTION,
  MatchedStatusEnum.ERROR,
];

export const listMatchedFlag = [
  MatchedFlagEnum.TOL,
  MatchedFlagEnum.BRF,
  MatchedFlagEnum.SUP,
  MatchedFlagEnum.CST,
  MatchedFlagEnum.ESP,
  MatchedFlagEnum.SIR,
  MatchedFlagEnum.GRP,
  MatchedFlagEnum.OTH,
  MatchedFlagEnum.DUP,
  MatchedFlagEnum.REJ,
  MatchedFlagEnum.UNM,
];

export const listMatchedTeam = [
  MatchedTeamEnum.PRO,
  MatchedTeamEnum.RES,
  MatchedTeamEnum.OCS,
  MatchedTeamEnum.FIN,
  MatchedTeamEnum.LDG,
  MatchedTeamEnum.FLI,
];

export const listEspWorkFlowStatusTabReview = [
  EspWorkflowStatusEnum.EXPORTED,
  EspWorkflowStatusEnum.COMPLETED,
  EspWorkflowStatusEnum.UNMATCHED,
];

export const renderClassNameStatusApWorkflow = (value: string) => {
  switch (value) {
    case "Further Action":
      return "text-warning";
    case "To Confirm":
      return "text-primary";
    case "Error":
      return "text-error";
    default:
      return "text-success";
  }
};

export function roundCost(rawValue: number | null | undefined): number {
  if (rawValue == null || isNaN(rawValue)) return 0;
  return Math.round(rawValue * 100) / 100;
}

export const isEditMatchedByStatus = (sts: MatchedStatusEnum): boolean => {
  return [...listMatchedStatusTabReview, MatchedStatusEnum.MATCHED].includes(sts);
};

export const isEditEspWorkflowByStatus = (sts: EspWorkflowStatusEnum): boolean => {
  return sts === EspWorkflowStatusEnum.UNMATCHED;
};

export const getAllDisplayedColumnFilters = (columns: any[]): string[] => {
  const result: string[] = [];

  columns.forEach((col) => {
    if (col.isDisplay === 1 && Array.isArray(col.children)) {
      col.children.forEach((child: any) => {
        if (child.isDisplay === 1 && Array.isArray(child.columnFilters)) {
          result.push(...child.columnFilters);
        }
      });
    }
  });

  return result;
}
